import { ref, readonly } from 'vue'
import { consola } from 'consola'

const logger = consola.withTag('support-sound')

/**
 * Звуковые оповещения чата поддержки (админка): «дин-дон» на новое обращение,
 * короткий «блип» на сообщение клиента.
 *
 * Звук синтезируется Web Audio API — без бинарных ассетов и внешних запросов.
 * Autoplay-политика браузера не даёт звучать до первого жеста пользователя:
 * AudioContext создаётся/возобновляется первым кликом или клавишей, до этого
 * оповещения молча пропускаются (тост при этом всё равно показывается).
 *
 * Состояние — на уровне модуля (как у useSupportSocket): включённость общая
 * для всех использований и переживает перемонтирование страницы.
 */

const STORAGE_KEY = 'adminSupportSoundEnabled'

const isEnabled = ref(true)
let audioContext: AudioContext | null = null
let unlockBound = false
let storageLoaded = false

interface Tone {
  freq: number
  /** Задержка старта от начала оповещения, сек */
  at: number
  duration: number
  peak: number
}

function loadEnabled(): void {
  if (storageLoaded || import.meta.server) return
  storageLoaded = true
  isEnabled.value = localStorage.getItem(STORAGE_KEY) !== '0'
}

function ensureContext(): AudioContext | null {
  if (import.meta.server) return null
  const Ctor = window.AudioContext
    ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctor) return null
  audioContext ??= new Ctor()
  if (audioContext.state === 'suspended') audioContext.resume().catch(() => {})
  return audioContext
}

/** Разблокировка аудио первым жестом — иначе браузер не даст звучать вовсе */
function bindUnlock(): void {
  if (unlockBound || import.meta.server) return
  unlockBound = true
  const unlock = (): void => { ensureContext() }
  window.addEventListener('pointerdown', unlock, { once: true, capture: true })
  window.addEventListener('keydown', unlock, { once: true, capture: true })
}

function schedule(context: AudioContext, tones: Tone[]): void {
  for (const tone of tones) {
    const oscillator = context.createOscillator()
    const gain = context.createGain()
    // Треугольная волна вместо синуса: гармоники дают «телесность», на колонках
    // ноутбука воспринимается заметно громче при том же уровне
    oscillator.type = 'triangle'
    oscillator.frequency.value = tone.freq
    const start = context.currentTime + tone.at
    // Быстрая атака и экспоненциальное затухание — «колокольчик» без щелчков
    gain.gain.setValueAtTime(0.0001, start)
    gain.gain.exponentialRampToValueAtTime(tone.peak, start + 0.012)
    gain.gain.exponentialRampToValueAtTime(0.0001, start + tone.duration)
    oscillator.connect(gain)
    gain.connect(context.destination)
    oscillator.start(start)
    oscillator.stop(start + tone.duration + 0.05)
  }
}

function play(tones: Tone[]): void {
  if (!isEnabled.value) return
  const context = ensureContext()
  // suspended = жеста ещё не было: не планируем звук, иначе накопленное
  // грянуло бы разом после разблокировки
  if (!context || context.state !== 'running') return
  try {
    schedule(context, tones)
  } catch (error) {
    logger.warn('Не удалось проиграть оповещение:', error)
  }
}

export function useSupportSound() {
  loadEnabled()
  bindUnlock()

  /** Новое обращение: двухнотный «дин-дон», заметный */
  function playTicket(): void {
    // peak 0.9 — практический потолок: выше начнётся клиппинг на сложении нот
    // (вторая стартует, пока первая ещё дозвучивает). Дальше громкость крутится
    // только системным регулятором
    play([
      { freq: 880, at: 0, duration: 0.45, peak: 0.9 },
      { freq: 1318.5, at: 0.15, duration: 0.7, peak: 0.9 },
    ])
  }

  /** Сообщение клиента: короткий «блип» — тише тикета, но отчётливый */
  function playMessage(): void {
    play([{ freq: 660, at: 0, duration: 0.25, peak: 0.7 }])
  }

  function toggle(): void {
    isEnabled.value = !isEnabled.value
    if (!import.meta.server) localStorage.setItem(STORAGE_KEY, isEnabled.value ? '1' : '0')
    // Прослушка при включении: сразу слышно, что звук работает и какой громкости.
    // Клик по кнопке — это и есть жест, разблокирующий AudioContext
    if (isEnabled.value) playTicket()
  }

  return { isSoundEnabled: readonly(isEnabled), toggle, playTicket, playMessage }
}
